import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { getChildAfpRequiredDirectReferralsForLevel, getChildAfpTeamWithdrawalRules } from "../../config/autoFillChildWithdrawalConfig";
import {
  buildCompletedTrancheLevelSet,
  checkUltraPlusMembership,
  getWithdrawalSixPlusRequirements,
  primaryTrancheRequirementBlockers,
  sequentialTrancheBlockers,
  filterWithdrawalsForProgram,
} from "../../config/autoFillWithdrawalSequencePolicy";
import {
  countQualifiedDirectReferrals,
  countQualifiedDirectReferralsForWithdrawal,
  countQualifiedTeamAfpMembers,
} from "./autoFillQualification";
import { ULTRA_DIRECT_REFERRAL_POLICY } from "../../config/autoFillLevelQualificationConfig";
import { isRootAutoFillProgram } from "./autoFillProgramSpawn";

export async function countUserWithdrawalsByStatus(userId, status) {
  const snap = await getDocs(
    query(
      collection(db, "autoFillWithdrawals"),
      where("userId", "==", userId),
      where("walletType", "==", "POINTS")
    )
  );
  return snap.docs.filter((d) => d.data().status === status).length;
}

async function getPointsWithdrawalsForUser(userId) {
  const snap = await getDocs(
    query(
      collection(db, "autoFillWithdrawals"),
      where("userId", "==", userId),
      where("walletType", "==", "POINTS")
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function getActiveProgramForUser(userId) {
  const snap = await getDocs(
    query(
      collection(db, "autoFillPrograms"),
      where("userId", "==", userId),
      where("status", "in", ["active", "on_hold"])
    )
  );
  if (snap.empty) return null;
  const programs = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort(
      (a, b) =>
        (Number(a.enrollmentSeq) || 0) - (Number(b.enrollmentSeq) || 0)
    );
  const root = programs.find((p) => !p.parentProgramId);
  return root || programs[0];
}

async function getProgramForWithdrawal(userId, sourceProgramId) {
  if (sourceProgramId && userId) {
    const snap = await getDocs(
      query(
        collection(db, "autoFillPrograms"),
        where("userId", "==", userId),
        where("programId", "==", sourceProgramId)
      )
    );
    if (!snap.empty) {
      const row = snap.docs[0];
      return { id: row.id, ...row.data() };
    }
  }
  return userId ? await getActiveProgramForUser(userId) : null;
}

/**
 * @param {object} [options]
 * @param {number} [options.withdrawalProgramLevel] — CO tranche level (withdrawal N = level N)
 */
export async function evaluateAutoFillWithdrawalEligibility(
  userProfile,
  userDocId,
  sourceProgramId = null,
  options = {}
) {
  const userId = userProfile?.uid;
  const reasons = [];

  const allWithdrawals = userId ? await getPointsWithdrawalsForUser(userId) : [];
  const pending = allWithdrawals.filter((w) => w.status === "PENDING").length;
  if (pending > 0) {
    reasons.push("You have a pending withdrawal. Wait for admin approval first.");
  }

  const program = userId
    ? await getProgramForWithdrawal(userId, sourceProgramId)
    : null;
  if (!program) {
    reasons.push("No active Auto Fill program found.");
  }
  if (program && program.status && !["active", "on_hold"].includes(program.status)) {
    reasons.push("This Auto Fill program is not active.");
  }

  const currentProgramLevel = program?.currentLevel || 1;
  const trancheLevel =
    options.withdrawalProgramLevel != null
      ? Number(options.withdrawalProgramLevel)
      : currentProgramLevel;
  const programLevel = trancheLevel || currentProgramLevel;

  if (
    program &&
    options.withdrawalProgramLevel != null &&
    currentProgramLevel < programLevel
  ) {
    reasons.push(
      `Reach program level ${programLevel} before withdrawing the level-${programLevel} CO Points tranche.`
    );
  }

  const isPrimaryAfp = program ? isRootAutoFillProgram(program) : true;
  const completedRows = allWithdrawals.filter((w) => w.status === "COMPLETED");
  const completedLevels = buildCompletedTrancheLevelSet(completedRows, program);
  const completedCount = filterWithdrawalsForProgram(
    completedRows,
    program
  ).length;

  let directCount = 0;
  let requiredDirect = 0;
  let teamCount = 0;
  let requiredTeam = 0;
  let teamRules = null;

  if (program && !isPrimaryAfp) {
    reasons.push(...sequentialTrancheBlockers(programLevel, completedLevels));

    requiredDirect = getChildAfpRequiredDirectReferralsForLevel(programLevel);
    if (requiredDirect > 0 && userDocId) {
      directCount = await countQualifiedDirectReferrals(userDocId);
      if (directCount < requiredDirect) {
        reasons.push(
          `Direct referrals (active AFP): ${directCount} / ${requiredDirect} required for child withdrawal ${programLevel} on ${program.programId}.`
        );
      }
    }

    teamRules = getChildAfpTeamWithdrawalRules(programLevel);
    requiredTeam = teamRules?.minTotal ?? 0;
    if (requiredTeam > 0 && teamRules && userDocId) {
      teamCount = await countQualifiedTeamAfpMembers(
        userDocId,
        userId,
        programLevel,
        { teamRules }
      );
      if (teamCount < requiredTeam) {
        reasons.push(
          `Team size (active AFP at depth ${teamRules.depth}, max ${teamRules.maxPerLeg} per level-1 leg — 50% cap): ${teamCount} / ${requiredTeam} required for child withdrawal ${programLevel}.`
        );
      }
    }
  } else if (program) {
    reasons.push(
      ...primaryTrancheRequirementBlockers(
        programLevel,
        userProfile,
        completedLevels
      )
    );

    if (programLevel >= 6 && programLevel <= 15 && userDocId) {
      const req = getWithdrawalSixPlusRequirements(programLevel);
      requiredDirect = req?.requiredDirect ?? 0;
      requiredTeam = req?.requiredTeam ?? 0;
      teamRules = req?.teamRules ?? null;

      directCount = await countQualifiedDirectReferralsForWithdrawal(
        userDocId,
        programLevel
      );
      if (requiredDirect > 0 && directCount < requiredDirect) {
        reasons.push(
          `Direct Ultra members: ${directCount} / ${requiredDirect} required for withdrawal ${programLevel}.`
        );
      }

      if (requiredTeam > 0 && teamRules) {
        teamCount = await countQualifiedTeamAfpMembers(
          userDocId,
          userId,
          programLevel
        );
        if (teamCount < requiredTeam) {
          reasons.push(
            `Team size (Ultra members at depth ${teamRules.depth}, max ${teamRules.maxPerLeg} per level-1 leg — 50% cap): ${teamCount} / ${requiredTeam} required for withdrawal ${programLevel}.`
          );
        }
      }
    }
  }

  return {
    eligible: reasons.length === 0 && pending === 0,
    status: reasons.length === 0 && pending === 0 ? "QUALIFIED" : "NOT QUALIFIED",
    reasons,
    programLevel,
    trancheLevel: programLevel,
    programId: program?.programId || null,
    isPrimaryAfp,
    gates: {
      ultraPlus: {
        required: programLevel === 4 || programLevel === 5,
        met:
          programLevel !== 4 && programLevel !== 5
            ? true
            : checkUltraPlusMembership(userProfile),
      },
    },
    directReferrals: {
      required: requiredDirect,
      current: directCount,
      policy:
        isPrimaryAfp && programLevel >= 6
          ? ULTRA_DIRECT_REFERRAL_POLICY
          : !isPrimaryAfp
            ? "Child AFP: direct referrals with active ₹150 Auto Fill program."
            : null,
    },
    teamAfp: {
      required: requiredTeam,
      current: teamCount,
      depth: teamRules?.depth ?? null,
      maxPerLeg: teamRules?.maxPerLeg ?? null,
      policy: teamRules
        ? isPrimaryAfp
          ? `Withdrawal ${programLevel}: count depth-${teamRules.depth} Ultra members under each level-1 leg; max ${teamRules.maxPerLeg} per leg (50%) toward ${teamRules.minTotal}.`
          : `Child withdrawal ${programLevel}: depth-${teamRules.depth} active AFP; max ${teamRules.maxPerLeg} per leg (50%) toward ${teamRules.minTotal}.`
        : null,
    },
    completedTrancheLevels: [...completedLevels].sort((a, b) => a - b),
    stats: {
      directCount,
      completedWithdrawals: completedCount,
      pendingWithdrawals: pending,
      ultraPlusApproved: checkUltraPlusMembership(userProfile),
    },
    nextSequence: completedCount + 1,
  };
}
